import { CircleSize, TodoStatusCircle } from "arkh";
import React, { useCallback, PropsWithChildren } from "react";
import { Insets, Platform, Pressable } from "react-native";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { TodoCircleStatusWithResponse_todo$key } from "./__generated__/TodoCircleStatusWithResponse_todo.graphql";
import * as Haptic from "expo-haptics";
import { useTodoSetStatusGlobalyDebounced } from "./useTodoSetStatus";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "react-native-reanimated";

export const TodoCircleStatusWithResponse = React.memo(
  TodoCircleStatusWithResponseC
);
const hitSlop = {
  left: 20,
  top: 25,
  right: 30,
  bottom: 15,
};

function TodoCircleStatusWithResponseC(props: {
  todo: TodoCircleStatusWithResponse_todo$key;
  onStatusUpdateNeedsAction(focusResponse: boolean): void;
  circleSize?: CircleSize;
  disabled?: boolean;
}) {
  const {
    id,
    expireAt,
    status: remoteStatus,
    canChangeStatus,
    canWrite,
    response,
    ordinal,
  } = useFragment(
    graphql`
      fragment TodoCircleStatusWithResponse_todo on Todo {
        id
        expireAt
        status
        canChangeStatus
        canWrite
        ordinal
        isResponseCompleted
        response {
          ... on TodoResponseNumber {
            __typename
          }
          ... on TodoResponseImage {
            __typename
            images {
              id
              ...Image_image
            }
          }
          ... on TodoResponseText {
            __typename
          }
        }
      }
    `,
    props.todo
  );

  const updateRemoteStatus = useTodoSetStatusGlobalyDebounced(id);

  const toggleStatus = useCallback(() => {
    const nextStatus = remoteStatus === "DONE" ? "PENDING" : "DONE";

    updateRemoteStatus(nextStatus, ordinal);
  }, [remoteStatus, updateRemoteStatus, ordinal]);

  return (
    <PressableWithScaleAndHaptics
      hitSlop={hitSlop}
      disabled={!canWrite || !canChangeStatus || props.disabled}
      currentStatus={remoteStatus}
      onPress={() => {
        if (!canChangeStatus) {
          props.onStatusUpdateNeedsAction(response?.__typename ? true : false);
        } else {
          toggleStatus();
        }
      }}
    >
      <TodoStatusCircle
        expireAtISO={expireAt}
        response={response}
        status={remoteStatus}
        size={props.circleSize ?? "smallMedium"}
      />
    </PressableWithScaleAndHaptics>
  );
}

function PressableWithScaleAndHaptics(
  props: PropsWithChildren<{
    onPress: () => void;

    hitSlop?: Insets;
    disabled?: boolean;
    children: React.ReactNode;
    currentStatus: "PENDING" | "DONE";
  }>
) {
  const pressScale = useSharedValue(1);
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: pressScale.value }],
  }));
  return (
    <Pressable
      hitSlop={props.hitSlop}
      disabled={props.disabled}
      onPress={() => {
        onPressHaptic();
        props.onPress();
      }}
      style={{
        opacity: props.disabled ? 0.3 : 1,
      }}
      onPressIn={() => {
        onPressInHaptic(props.currentStatus);
        pressScale.value = withSpring(0.8, {
          damping: 100,
          stiffness: 2000,
        });
      }}
      onPressOut={() => {
        pressScale.value = withSpring(1, {
          damping: 50,
          stiffness: 1500,
        });
      }}
    >
      <Animated.View style={animatedStyle}>{props.children}</Animated.View>
    </Pressable>
  );
}
function onPressInHaptic(currentStatus: "PENDING" | "DONE") {
  if (Platform.OS === "ios") {
    Haptic.selectionAsync().catch(() => {});
  } else if (Platform.OS === "android") {
    const nextState = currentStatus === "PENDING" ? "DONE" : "PENDING";

    Haptic.performAndroidHapticsAsync(
      nextState === "PENDING"
        ? Haptic.AndroidHaptics.Toggle_Off
        : Haptic.AndroidHaptics.Toggle_On
    ).catch(() => {});
  }
}

function onPressHaptic() {
  if (Platform.OS === "ios") {
    setTimeout(() => {
      Haptic.impactAsync(Haptic.ImpactFeedbackStyle.Soft).catch(() => {});
    }, 100);
  }
}
